import { TechnicalError } from '@orus.eu/error'
import type { Product, ProductFamily } from '@orus.eu/product'

import type { AbstractDimension } from '../dimension/abstract-dimension.ts'
import type { BooleanDimension } from '../dimension/boolean-dimension.ts'

/**
 * Browser-safe descriptor for one product's dimensions. Every field adopts an existing dimension object by identity;
 * nothing here creates a dimension.
 */
export type ProductDimensionsModule = {
  readonly product: Product
  readonly family: ProductFamily | null
  /** Named `<product>Selected` by construction. */
  readonly selectionDimension: BooleanDimension<`${string}Selected`>
  /** Model-version dimension, normalized from the regular/legacy input slot. */
  readonly versionDimension: AbstractDimension
  /** Named `<product>PricingVersion` for the products that have one. */
  readonly pricingVersionDimension: AbstractDimension
  readonly quoteV2Dimension?: AbstractDimension
  readonly quoteV3Dimension?: AbstractDimension
}

/**
 * Mutually-exclusive union: `legacyVersionDimension` is an explicit escape hatch for the few products whose version
 * dimension keeps an irregular name; renaming a live dimension would break existing data, so that slot stays
 * permanent. New products use `productVersionDimension`.
 */
type VersionSlot =
  | { readonly productVersionDimension: AbstractDimension; readonly legacyVersionDimension?: never }
  | { readonly legacyVersionDimension: AbstractDimension; readonly productVersionDimension?: never }

export type DefineProductDimensionsInput = {
  readonly product: Product
  readonly family: ProductFamily | null
  readonly selectionDimension: BooleanDimension<`${string}Selected`>
  readonly pricingVersionDimension: AbstractDimension
  readonly quoteV2Dimension?: AbstractDimension
  readonly quoteV3Dimension?: AbstractDimension
} & VersionSlot

/**
 * Precise descriptor produced by {@link defineProductDimensions} for a specific input `I`, keeping each adopted
 * dimension's concrete type. Always assignable to the erased {@link ProductDimensionsModule} supertype that
 * consumers depend on.
 */
export type ProductDimensionsModuleOf<I extends DefineProductDimensionsInput> = {
  readonly product: I['product']
  readonly family: I['family']
  readonly selectionDimension: I['selectionDimension']
  readonly versionDimension: NormalizedVersionDimensionOf<I>
  readonly pricingVersionDimension: I['pricingVersionDimension']
} & PresentOptionalSlotsOf<I>

type OptionalSlotKey = 'quoteV2Dimension' | 'quoteV3Dimension'

/** Keeps only the optional slots the caller actually provided, each with its precise type (dropped otherwise). */
type PresentOptionalSlotsOf<I extends DefineProductDimensionsInput> = {
  readonly [K in OptionalSlotKey as K extends keyof I ? (I[K] extends undefined ? never : K) : never]: K extends keyof I
    ? I[K]
    : never
}

/** Mirrors the runtime `productVersionDimension ?? legacyVersionDimension` normalization at the type level. */
type NormalizedVersionDimensionOf<I extends DefineProductDimensionsInput> = I extends {
  readonly productVersionDimension: infer D extends AbstractDimension
}
  ? D
  : I extends { readonly legacyVersionDimension: infer D extends AbstractDimension }
    ? D
    : never

function camelizeProductCode(product: Product): string {
  return product.replace(/-./g, (hyphenAndLetter) => hyphenAndLetter.slice(1).toUpperCase())
}

function checkDimensionName(product: Product, slot: string, actualName: string, expectedName: string): void {
  if (actualName !== expectedName) {
    throw new TechnicalError('Product-module dimension name does not match the descriptor product', {
      context: { product, slot, expectedName, actualName },
    })
  }
}

/**
 * Definition-site constructor. Pure: normalizes the version slot and returns a frozen descriptor. The value it adds is
 * the call-site type-check (adoption slot shapes, `<string>Selected` selection name) + object-identity preservation.
 */
export function defineProductDimensions<const I extends DefineProductDimensionsInput>(
  input: I,
): ProductDimensionsModuleOf<I>
export function defineProductDimensions(input: DefineProductDimensionsInput): ProductDimensionsModule {
  // A copy-pasted descriptor with a stale `product` would otherwise compile cleanly: nothing ties the code string to
  // the adopted dimension objects, so this check catches it at definition time. The legacy version slot is exempt by
  // design: it exists for the few products with an irregular version-dimension name.
  const camel = camelizeProductCode(input.product)
  checkDimensionName(input.product, 'selection', input.selectionDimension.name, `${camel}Selected`)
  checkDimensionName(input.product, 'pricing-version', input.pricingVersionDimension.name, `${camel}PricingVersion`)
  if (input.productVersionDimension !== undefined) {
    checkDimensionName(input.product, 'product-version', input.productVersionDimension.name, `${camel}ProductVersion`)
  }

  const versionDimension = input.productVersionDimension ?? input.legacyVersionDimension
  // Optional slots are included only when present so the descriptor satisfies `exactOptionalPropertyTypes`.
  return {
    product: input.product,
    family: input.family,
    selectionDimension: input.selectionDimension,
    versionDimension,
    pricingVersionDimension: input.pricingVersionDimension,
    ...(input.quoteV2Dimension !== undefined ? { quoteV2Dimension: input.quoteV2Dimension } : {}),
    ...(input.quoteV3Dimension !== undefined ? { quoteV3Dimension: input.quoteV3Dimension } : {}),
  }
}
